import React, { useEffect, useMemo, useState } from 'react';
import { Header, Table, Field, Button, Message, Dropdown, Modal, Stat, Checkbox, Input, Toolbar, Groupper, SegmentControl, useFrontier, Pagination, Placeholder, Icon, IconName } from 'react-frontier2';
import { EventDate, PaymentMethods, SetLoading } from '@arema/components/Classes';
import { Link } from 'react-router-dom';
import { addCommas, bindClick, bindFormChange, fromUnix, getNowLocal, toCents, toggleArray } from '@arema/components/Util';
import { Trans, useTranslation } from 'react-i18next';
import { BankAccount, EventSectionProps, PayoutType } from '../../AdminClasses';
import { BankAccountModal, EventDateStatusModal, EventDepositSourcesModal } from '../../components';
import { useUser } from '../../AdminHooks';
import { firstBy } from 'thenby';
import { BalanceDateSales, BalancePayout, EventBusinessSource } from '@arema/types/internal';
import { useAPI } from '@arema/components/Hooks';
import { DateTime } from 'luxon';
import API from '../../API';
import UserAccess from '../../UserAccess';
import Validator from '@arema/components/Validator';
import classNames from 'classnames';

interface CreatePayout{
	payout_type?: number,
	account_id?: number,
	date_scheduled: number,
	comments?: string,
	completed?: boolean,
	scheduled?: boolean,
	business_id: number,
	amounts: {
		date_id: number,
		date: number,
		amount: string
	}[]
}

enum ModalType{
	METHOD_INFO = 1,
	BANK_ACCOUT_CREATE = 2,
	FILTER_DATES = 3,

	PAYOUT_CREATE = 10,
	PAYOUT_VIEW = 11,
	DATE_SECTION_STATUS = 12,
	DEPOSIT_SOURCES = 13,
}

enum FilterType{
	DATES = 1,
	RANGE = 2,
}

const PAGE_SIZE = 15;
const PAYOUTS_PAGE_SIZE = 15;

var ConditionalIcon = (props: { name: IconName, color: string, if: boolean, value: any })=>{
	if(props.if===true) return <Icon name={props.name} color={props.color} />
	else return props.value
}

var EventBalance = (props: EventSectionProps)=>{
	var { hasAccess } = useUser();
	var { t } = useTranslation();
	var { Toast } = useFrontier();
	var [dates, setDates] = useState<BalanceDateSales[]>(null);
	var [payouts, setPayouts] = useState<BalancePayout[]>(null);
	var [modalPrompts, setModalPrompts] = useState<string[]>(null);
	var [loadError, setLoadError] = useState<string>(null);
	var [shownModal, setShownModal] = useState<ModalType>(null);
	var [selectedMethod, setSelectedMethod] = useState<number>(null);
	var [selectedPayout, setSelectedPayout] = useState<BalancePayout>(null);
	var [selectedDate, setSelectedDate] = useState<BalanceDateSales>(null);
	var [bankAccounts, setBankAccounts] = useState<BankAccount[]>(null)
	var [payoutForm, setPayoutForm] = useState<CreatePayout>(null);
	var [createAccountModal, setCreateAccountModal] = useState<boolean>(false);
	var [selectedDates, setSelectedDates] = useState<number[]>(null);
	var [payoutsPage, setPayoutsPage] = useState<number>(0);
	var [fetchDeposits, setFetchDeposits] = useState<boolean>(false);
	var { load: loadBusiness, data: businessSources, loadError: businessLoadError } = useAPI(API.getEventBusinessSources);

	var [allDates, setAllDates] = useState<EventDate[]>(null);
	var [filtered, setFiltered] = useState<boolean>(false);
	var [filterType, setFilterType] = useState<FilterType>(FilterType.DATES);
	var [filterPage, setFilterPage] = useState<number>(0);
	var [filterRange, setFilterRange] = useState<{
		start: DateTime,
		end: DateTime,
	}>(null);

	var getData = (force_deposits: boolean=false)=>{
		setDates(null)
		setPayouts(null);
		setPayoutsPage(0);
		API.getEventBalance(props.event.event_id, true, fetchDeposits || force_deposits, true).then(res=>{
			if(res.error) return setLoadError(res.message);
			setPayouts(res.data.payouts)
			setDates(res.data.dates);
		}).catch(err=>{
			setLoadError('Hubo un error inesperado cargando el estado de cuenta. (LCL-1)')
		});
	}
	
	var loadConcilia = ()=>{
		setFetchDeposits(true);
		getData(true);
	}

	useEffect(()=>{
		if(!dates) getData();

		var now = getNowLocal();
		setFilterRange({
			start: now.plus({ hour: -24 }),
			end: now,
		});
	}, []);

	var viewMethod = (method_id: number)=>{
		return ()=>{
			setSelectedMethod(method_id);
			setShownModal(ModalType.METHOD_INFO);
		}
	}

	var viewPayout = (payout: BalancePayout)=>{
		return ()=>{
			setSelectedPayout(payout);
			setShownModal(ModalType.PAYOUT_VIEW);
		}
	}

	var showCreatePayout = ()=>{
		if(!hasAccess(UserAccess.EVENTS.CREATE_PAYOUT)) return;
		if(bankAccounts===null){
			API.getPromoterBankAccounts(props.event.promoter_id).then(res=>{
				if(res.error){
					return Toast.error(`Hubo un error inesperado cargando las cuentas bancarias del promotor: ${res.message}`);
				}
				setBankAccounts(res.data);
			}).catch(err=>{
				Toast.error('Hubo un error inesperado cargando las cuentas bancarias del promotor (LCL-1)');
			});
		}
		if(!businessSources){
			loadBusiness(props.event.event_id);
		}
		setPayoutForm({ amounts: [], date_scheduled: null, business_id: null });
		setShownModal(ModalType.PAYOUT_CREATE);
	}

	var showAddAccount = ()=>{
		if(!hasAccess(UserAccess.CATALOGS.PROMOTER_ADD_BANK)){
			return Toast.error('No tienes permiso a realizar esta acción.');
		}
		setCreateAccountModal(true);
	}

	var showDateSections = (date_id: number)=>{
		return ()=>{
			setSelectedDate(dates.find(a=>a.date_id==date_id));
			setShownModal(ModalType.DATE_SECTION_STATUS);
		}
	}

	var onBankAccountCreated = (account: BankAccount)=>{
		if(!bankAccounts){
			setBankAccounts([account]);
		}else{
			setBankAccounts([
				...bankAccounts,
				account
			]);
		}
		if(payoutForm){
			setPayoutForm({
				...payoutForm,
				account_id: account.account_id,
			});
		}
		setCreateAccountModal(false);
	}

	var showFilterDates = ()=>{
		setSelectedDates([]);
		setFilterPage(0);
		setShownModal(ModalType.FILTER_DATES);
		if(!allDates){
			setModalPrompts(null);
			API.getEventDates(props.event.event_id).then(res=>{
				if(res.error) return setModalPrompts([res.message]);
				setAllDates(res.data);
			}).catch(err=>{
				return setModalPrompts([t('common.unexpected_code', { code: 'LCL-EVBLSHFL-1' })]);
			});
		}
	}

	var filterDates = ()=>{
		setFiltered(true);
		setShownModal(null);
	}

	var createPayout = (setLoading: SetLoading)=>{
		var { valid, prompts } = Validator(payoutForm, {
			amounts: [{
				rule: (v: CreatePayout["amounts"])=>{
					var amt = 0;
					for(var i of v){
						amt += parseFloat(i.amount);
					}
					return amt>0;
				}, 
				prompt: 'Favor de ingresar por lo menos una liquidación'
			}, {
				rule: (v: CreatePayout["amounts"])=>{
					for(var i of v){
						var date = dates.find(a=>a.date_id==i.date_id);
						if(Number.isNaN(parseFloat(i.amount))) return false;
						return parseFloat(i.amount)>0;
						// var total_income = payoutForm.payout_type===PayoutType.RETORNO ? date.concilia.total : date.concilia.total_tickets
						// var total_remaining = total_income-date.total_payout;
						// if(parseFloat(i.amount)>total_remaining) return false;
					}
					return true;
				},
				prompt: 'Uno o mas calendarios están liquidando una cantidad inválida.'
			}],
			payout_type: [{
				rule: 'empty', prompt: 'Favor de seleccionar el tipo de liquidación'
			}],
			account_id: [{
				rule: 'empty',
				if: !payoutForm.completed,
				prompt: 'Favor de seleccionar la cuenta bancaria a transferir.'
			}],
			date_scheduled: [{
				rule: (v: number)=>{
					return v && getNowLocal().startOf('day').toSeconds()<=v;
				},
				if: payoutForm.scheduled && !payoutForm.completed,
				prompt: 'Favor de ingresar una fecha agendada futura.'
			}, {
				rule: (v: number)=>{
					if(!v) return false;
					return getNowLocal().endOf('day').toSeconds()>=v;
				},
				if: payoutForm.completed,
				prompt: 'Favor de seleccionar una fecha pasada del deposito.'
			}]
		});
		setModalPrompts(prompts);
		if(!valid) return;
		
		setLoading(true);
		API.createPayout(props.event.event_id, {
			payout_type: payoutForm.payout_type,
			account_id: payoutForm.completed ? null : payoutForm.account_id,
			comments: payoutForm.comments && payoutForm.comments.length>0 ? payoutForm.comments : null,
			completed: payoutForm.completed,
			date_scheduled: payoutForm.completed || payoutForm.scheduled ? payoutForm.date_scheduled : null,
			dates: payoutForm.amounts.map(a=>({
				date_id: a.date_id,
				amount: parseFloat(a.amount),
			}))
		}).then(res=>{
			if(res.error) return setModalPrompts([res.message]);
			setShownModal(null);
			setDates(null);
			setPayouts(null);
			setPayoutsPage(0);
			Toast.success('Se ha creado la liquidación con éxito');
			setTimeout(()=>{
				getData();
			}, 1000);
		}).catch(err=>{
			setModalPrompts(['Hubo un error inesperado creando la liquidación (LCL-1)']);
		}).finally(()=>{
			setLoading(false);
		})
	}

	var getSalesReport = ()=>{
		var tix = Toast.show('Descargando reporte de ventas...', {
			dismissable: false,
			loading: true,
		});
		API.getEventSalesReport(props.event.event_id, filtered ? filtered_dates.map(a=>a.date_id) : null).then(res=>{
			if(res.error) return Toast.error(res.message);
			if(!res.data.url) return Toast.error(t('common.unexpected_code', { code: 'LCL-EVBLGSLRP-2' }));
			setTimeout(()=>{
				window.open(res.data.url, '_blank');
			});
		}).catch(err=>{
			Toast.error(t('common.unexpected_code', { code: 'LCL-EVBLGSLRP-1' }));
		}).finally(()=>{
			Toast.dismiss(tix);
		})
	}

	var now = useMemo(()=>{
		return getNowLocal();
	}, []);

	var filter_prediction = useMemo(()=>{
		if(!dates || !filterRange) return 0;
		var date_start = filterRange.start, date_end = filterRange.end;
		if(!date_start || !date_end) return 0;

		if(date_start>date_end){
			var t = date_start;
			date_start = date_end;
			date_end = t;
		}

		var ds = filterRange.start.toSeconds();
		var de = filterRange.end.toSeconds();

		return dates.filter(a=>a.date>=ds && a.date<=de).length;
	}, [dates, filterRange]);

	var {
		filtered_dates,
		filtered_payouts,
		total_payment_methods,
		calendario_count,
		total_money,
		total_tickets,
		total_commission,
		total_deposit,
		total_deposit_tickets,
		total_payout,
		total_payout_return,
		total_remaining,
		total_printed,
		total_printed_commission,
		total_free,
		ticket_count,
		total_commission_return,
		viewing_method,
	} = useMemo(()=>{
		var total_payment_methods : BalanceDateSales["methods"] = [];
		var total_money = dates ? 0 : null;
		var total_tickets = dates ? 0 : null;
		var total_commission = dates ? 0 : null;
		var total_deposit = dates ? 0 : null;
		var total_deposit_tickets = dates ? 0 : null;
		var total_payout = dates ? 0 : null;
		var total_payout_return = dates ? 0 : null;
		var total_remaining = dates ? 0 : null;
		var ticket_count = dates ? 0 : null;
		var total_commission_return = dates ? 0 : null;
		var total_printed = dates ? 0 : null;
		var total_printed_commission = dates ? 0 : null;
		var total_free = dates ? 0 : null;

		// var filtered_dates = filtered ? (selectedDates ?  : dates;
		var filtered_dates : typeof dates = [];
		if(!!dates){
			if(filtered){
				if(filterType===FilterType.DATES){
					filtered_dates = selectedDates ? dates.filter(a=>selectedDates.indexOf(a.date_id)>-1) : [];
				}else{
					if(!!filterRange){
						var ds = filterRange.start.toSeconds();
						var de = filterRange.end.toSeconds();
						filtered_dates = dates.filter(a=>a.date>=ds && a.date<=de);
					}
				}
			}else{
				filtered_dates = dates;
			}
		}
		var calendario_count = filtered_dates?.length;

		var filtered_payouts : BalancePayout[] = [];
		if(filtered){
			var sel_dates = filtered_dates.map(a=>a.date_id);
			for(let i of payouts){
				var has_date = i.dates.findIndex(a=>sel_dates.indexOf(a.date_id)!=-1)!=-1;
				if(!has_date) continue;
				var py = {...i};
				py.dates = py.dates.filter(a=>sel_dates.indexOf(a.date_id)!=-1);
				py.amount = py.dates.reduce((a,b)=>a+b.amount, 0);
				filtered_payouts.push(py);
			}
		}else{
			filtered_payouts = payouts;
		}

		filtered_payouts = filtered_payouts ? filtered_payouts.sort(firstBy('completed', 'asc').thenBy('date_completed', 'desc').thenBy('date_created', 'desc')) : [];

		if(!!dates){
			for(var i of filtered_dates){
				total_money += i.total;
				total_tickets += i.total_tickets;
				total_commission += i.total_commission;
				total_commission_return += i.total_commission_return;
				total_deposit += i.concilia.total_deposit;
				total_deposit_tickets += i.concilia.total_deposit_tickets;
				ticket_count += i.tickets;
				total_payout += i.total_payout;
				total_payout_return += i.total_payout_return;
	
				for(var m of i.methods){
					var pix = total_payment_methods.findIndex(a=>a.method_id==m.method_id);
					if(m.method_id===PaymentMethods.PRINTED_TICKET){
						total_printed += m.total_tickets;
						total_printed_commission += m.total_commission;
					}
					if(m.method_id===PaymentMethods.FREE){
						total_free += m.total_tickets;
					}
					if(pix===-1){
						total_payment_methods.push({
							...m
						});
					}else{
						total_payment_methods[pix].tickets += m.tickets;
						total_payment_methods[pix].total += m.total;
						total_payment_methods[pix].total_tickets += m.total_tickets;
						total_payment_methods[pix].total_commission += m.total_commission;
						total_payment_methods[pix].total_deposit += m.total_deposit;
					}
				}
			}
		}
	
		total_remaining = total_tickets - (total_payout - total_payout_return);
		var viewing_method = total_payment_methods.find(a=>a.method_id==selectedMethod);

		return {
			filtered_dates,
			filtered_payouts,
			total_payment_methods,
			calendario_count,
			total_money,
			total_tickets,
			total_commission,
			total_deposit,
			total_deposit_tickets,
			total_payout,
			total_payout_return,
			total_remaining,
			total_printed,
			total_printed_commission,
			total_free,
			ticket_count,
			total_commission_return,
			viewing_method,
		}
	}, [dates, selectedDates, filtered, filterType, selectedMethod]);

	var {
		business_sources,
		event_business,
	} = useMemo(()=>{
		var bsn : {
			business_id: number,
			business_name: string,
			ticket_cost: number,
			commission: number,
		}[] = [];
		var ebs : {
			business_id: number,
			business_name: string
		}[] = [];
		if(!!businessSources){
			if(payoutForm && payoutForm.amounts){
				var src = businessSources.filter(a=>payoutForm.amounts.findIndex(b=>b.date_id===a.date_id)!=-1);
				for(var i of src){
					var bx = bsn.findIndex(a=>a.business_id===i.business_id);
					if(bx==-1){
						bx = bsn.push({
							business_id: i.business_id,
							business_name: i.business_name,
							commission: 0,
							ticket_cost: 0,
						})-1;
					}
		
					bsn[bx].ticket_cost += i.ticket_cost;
					bsn[bx].commission += i.commission;
				}
			}

			for(var i of businessSources){
				var bx = ebs.findIndex(a=>a.business_id===i.business_id);
				if(bx==-1) ebs.push({
					business_id: i.business_id,
					business_name: i.business_name,
				});
			}
		}
		return {
			business_sources: bsn,
			event_business: ebs,
		};
	}, [businessSources, payoutForm?.amounts]);

	var onDateSelected = (date_id: number)=>{
		return ()=>{
			setSelectedDates(toggleArray(selectedDates, date_id));
		}
	}

	var AllDatesList = useMemo(()=>{
		if(!allDates) return [];
		var now = getNowLocal().toSeconds();
		var sorted_dates = allDates.sort(firstBy((a: EventDate, b: EventDate)=>{
			return (a.date>=now ? 0 : 1) - (b.date>=now ? 0 : 1)
		}).thenBy((a: EventDate, b: EventDate)=>{
			return a.date>=now ? (a.date-b.date) : (b.date-a.date);
		}));
		var date_slice = sorted_dates.slice(filterPage*PAGE_SIZE, (filterPage*PAGE_SIZE)+PAGE_SIZE);
		var items : React.ReactElement[] = [];
		var is_past = false;
		for(var a of date_slice){
			if(a.date<=now && !is_past){
				is_past = true;
				items.push((
					<Table.Row header selectable={false} key={`PSTHDR`}>
						<Table.Cell colSpan={100}>
							Fechas pasadas
						</Table.Cell>
					</Table.Row>
				))
			}
			var selected = selectedDates && selectedDates.indexOf(a.date_id)>-1;
			items.push((
				<Table.Row selectable collapsingIndexes={[0, 1]} onClick={onDateSelected(a.date_id)} data={[
					<Icon name={!selected ? 'circle' : 'check-circle'} solid={selected} style={{ marginRight: 0 }} />,
					a.date_id,
					fromUnix(a.date).toFormat('dd/MMM/yy HH:mm'),
					a.city,
				]} />
			))
		}
		return items;
	}, [allDates, filterPage, selectedDates, PAGE_SIZE]);

	var onPayoutFormChange = bindFormChange(payoutForm, setPayoutForm);

	if(loadError){
		return <Header text='Error' subtext={loadError} iconName='face-frown-open' />
	}

	return <div>
		{!!filtered && (
			<Message style={{ maxWidth: 500, margin: 'auto', marginTop: 10 }} type='info' header={t('event_balance.filtered_dates')} centered>
				{t('event_balance.filtered_dates_text')}
				<br />
				{filterType===FilterType.DATES ? (
					selectedDates.join(', ')
				) : <>
					<b>{filterRange?.start?.toFormat('dd/MMM/yy')}</b>
					<Icon name='arrow-right' style={{ marginLeft: 5 }} />
					<b>{filterRange?.end?.toFormat('dd/MMM/yy')}</b>
				</>}
				<br />
				<Button text={t('event_balance.empty_filter')} iconName='filter' size='small' color='blue' style={{ marginTop: 10 }} onClick={bindClick(setFiltered, false)} />
			</Message>
		)}
		<Groupper fitted
			title={'Detalles'} 
			style={{ margin: 'auto', marginTop: 10, maxWidth: 500 }}
			titleRight={(
				<Button text='Refrescar' size='tiny' onClick={()=>getData(!!fetchDeposits)} />
			)}
		>
			<Toolbar fitted>
				{hasAccess(UserAccess.EVENTS.CREATE_PAYOUT) && (
					<Toolbar.Item iconName='money-bill-wave' text='Liquidar' onClick={showCreatePayout} />
				)}
				<Toolbar.Item iconName='funnel-dollar' text='Filtrar' onClick={showFilterDates} />
				<Toolbar.Dropdown iconName='file-excel' text='Reportes' items={[
					{ text: 'Reporte ventas', onClick: getSalesReport, iconName: filtered ? 'filter' : null },
				]} />
			</Toolbar>
			<Table striped details>
				<Table.Head>
					{!fetchDeposits && (
						<Table.Row>
							<Table.Cell header colSpan={2}>
								<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
									<div>
										<Icon name='exclamation-triangle' />
										Conciliación no cargada
									</div>
									<Button size='tiny' text='Cargar' style={{ lineHeight: '16px' }} onClick={loadConcilia} />
								</div>
							</Table.Cell>
						</Table.Row>
					)}
				</Table.Head>
				<Table.Body>
					<Table.Row data={['ID', props.event.event_id]} />
					<Table.Row data={['Promotor', <>
						{props.event.promoter_name}
						<div className="meta">
							<Button size='tiny' color='black' text='Ver promotor' as={Link} to={`/promoters/${props.event.promoter_id}`} target={'_blank'} />
						</div>	
					</>]} />
					<Table.Row data={['Calendarios', <Placeholder width={30} random={false} value={calendario_count} check={!!dates} />]} />
					<Table.Row data={['Boletos', <Placeholder value={ticket_count} width={35} random={false} />]} />
					<Table.Divider />
					<Table.Row data={['Total ingresos', <Placeholder value={addCommas(total_money)} />]} />
					<Table.Row data={['Cargo servicio', <Placeholder value={addCommas(total_commission)} />]} />
					<Table.Row data={['Retorno estimado', <Placeholder value={props.event.commission_return > 0 ? addCommas(total_commission_return): <i>Sin Definir</i>} />]} />
					<Table.Row data={['Boleto duro', <Placeholder value={dates ? <>
						{addCommas(total_printed)}
						{total_printed>0 && <>
							<span style={{ fontSize: 12, color: 'gray', marginLeft: 5 }}>(No sumado)</span>
							<div className="meta">
								CxS: {addCommas(total_printed_commission)}
							</div>
						</>}
					</> : null} />]} />
					{fetchDeposits ? <>
						<Table.Divider />
						<Table.Row data={['Total conciliado', <Placeholder value={addCommas(total_deposit)} />]} />
						<Table.Row data={['Conciliado promotor', <Placeholder value={addCommas(total_deposit_tickets)} />]} />
					</> : null}
					<Table.Divider />
					<Table.Row data={['Ingresos promotor', <Placeholder value={addCommas(total_tickets)} />]} />
					<Table.Row data={['Liquidado', <Placeholder value={dates ? <>
						{addCommas(total_payout-total_payout_return)}	
					</> : null} />]} />
					{total_payout_return>0 && (
						<Table.Row data={['Retorno liquidado', addCommas(total_payout_return)]} />
					)}
					<Table.Row data={['Cantidad restante', <Placeholder value={(
						<div style={{ color: toCents(total_remaining)<0 ? 'brown' : 'black' }}>
							{toCents(total_remaining)<0 && (
								<Icon name='triangle-exclamation' />
							)}
							{addCommas(total_remaining)}
						</div>
					)} check={!!dates} />]} />
				</Table.Body>
			</Table>
		</Groupper>
		{hasAccess(UserAccess.CONTABILIDAD.VIEW_PAYOUTS) && !!payouts && payouts.length>0 && (
			<Table divided striped selectable style={{ margin: 'auto', marginTop: 15, maxWidth: 700 }}>
				<Table.Head title='Liquidaciones' />
				<Table.Body>
					{!!filtered && (
						<Table.Row header selectable={false}>
							<Table.Cell colSpan={100}>Mostrando solo liquidaciones filtradas</Table.Cell>
						</Table.Row>
					)}
					{filtered_payouts.slice(payoutsPage*PAYOUTS_PAGE_SIZE, (payoutsPage*PAYOUTS_PAGE_SIZE)+PAYOUTS_PAGE_SIZE).map(a=>{
						var scheduled_late = !a.completed && !!a.date_scheduled && now.toSeconds()>a.date_scheduled;
						return (
							<Table.Row key={`PYT-${a.payout_id}`} onClick={viewPayout(a)}>
								<Table.Cell collapsing>{a.payout_id}</Table.Cell>
								<Table.Cell collapsing className={classNames('collapsing',{
									red: scheduled_late
								})}>
									{!!!a.completed && !!a.date_scheduled && (
										<i className="calendar icon"></i>
									)}
									{fromUnix(a.date_completed || a.date_scheduled || a.date_created).toFormat('dd/MMM/yy HH:mm')}		
								</Table.Cell>
								<Table.Cell>{a.comments}</Table.Cell>
								<Table.Cell collapsing>{addCommas(a.amount)}</Table.Cell>
								<Table.Cell collapsing centered color={classNames({
									yellow: !a.completed && !scheduled_late && !a.approved,
									red: scheduled_late && !a.completed,
									green: !!a.completed
								}) as any}>
									<Icon name={classNames('icon', {
										clock: !a.completed && (!a.approved || !a.date_scheduled),
										calendar: !a.completed && a.approved && !!a.date_scheduled,
										check: a.completed
									}) as any} />
									{
										a.completed ? 'Completado' :
										!a.approved ? 'En aprobación' : 
										!!a.date_scheduled ? 'Agendado' :
										a.approved ? 'Pendiente' : null
									}
								</Table.Cell>
							</Table.Row>
						)
					})}
				</Table.Body>
				{filtered_payouts.length>PAYOUTS_PAGE_SIZE && (
					<Table.Footer>
						<Table.Cell row colSpan={100}>
							<div style={{ display: 'flex', justifyContent: 'flex-end' }}>
								<Pagination page={payoutsPage} onPageChange={setPayoutsPage} pageCount={Math.ceil(filtered_payouts.length/PAYOUTS_PAGE_SIZE)} />
							</div>
						</Table.Cell>
					</Table.Footer>
				)}
			</Table>
		)}
		{total_payment_methods.length>0 && (
			<Groupper fitted
				title='Formas de pago'
				style={{ margin: 'auto', marginTop: 15, maxWidth: 800 }}
			>
				<Toolbar stretch={false}>
					<Toolbar.Item iconName='file-invoice-dollar' text='Ver origenes' onClick={bindClick(setShownModal, ModalType.DEPOSIT_SOURCES)} />
				</Toolbar>
				<Table striped selectable divided 
					style={{ borderBottomRightRadius: 12, borderBottomLeftRadius: 12 }}
				>
					<Table.Head data={[['ID', 'Método', 'Boletos', 'Concilia', 'Promotor', 'Comisión', 'Total']]} centeredIndexes={[0, 2, 3]} />
					<Table.Body>
						{total_payment_methods.sort((a,b)=>a.method_id-b.method_id).map(a=>{
							var is_concilia = [PaymentMethods.FREE, PaymentMethods.PRINTED_TICKET, PaymentMethods.FREE_TICKET].indexOf(a.method_id)==-1;
							var hide_total = [PaymentMethods.FREE, PaymentMethods.FREE_TICKET].indexOf(a.method_id)!=-1;
							return <Table.Row 
								key={`PMT-${a.method_id}`} 
								onClick={viewMethod(a.method_id)}
								collapsingIndexes={[0, 2, 3, 4, 5, 6]}
								centeredIndexes={[
									0, 2, 3,
									...(!is_concilia ? [4] : []),
									...(hide_total ? [5, 6] : []),
								]}
								data={[
									a.method_id || <Icon name="minus" color='grey' />,
									a.method_name || <Icon name="minus" color='grey' />,
									addCommas(a.tickets, false),
									<ConditionalIcon name={fetchDeposits ? 'minus' : 'times'} color='gray' if={!fetchDeposits || !is_concilia} value={`${addCommas((a.total_deposit*100)/a.total)}%`} />,
									<ConditionalIcon name='minus' color='gray' if={!is_concilia} value={addCommas(a.total_tickets)} />,
									<ConditionalIcon name='minus' color='gray' if={hide_total} value={addCommas(a.total_commission)} />,
									<ConditionalIcon name='minus' color='gray' if={hide_total} value={addCommas(a.total)} />,
								]}
							/>
						})}
					</Table.Body>
				</Table>
			</Groupper>
		)}
		{!!dates && dates.length>0 && (
			<Table striped divided style={{ margin: 'auto', marginTop: 15, maxWidth: 800 }}>
				<Table.Head title='Calendarios' data={[
					['ID', 'Fecha', 'Boletos', 'Total', 'Comisión', 'Promotor', 'Liquidado', 'Liquidable']
				]} />
				<Table.Body>
					{filtered_dates.length==0 ? (
						<Table.Cell row empty>
							{t(`event_balance.no_dates${filtered ? '_filter' : ''}`)}
						</Table.Cell>
					) : filtered_dates.sort((a,b)=>a.date-b.date).map(a=>(
						<Table.Row selectable
							key={`DTTB-${a.date_id}`}
							onClick={showDateSections(a.date_id)}
							collapsingIndexes={[0, 2, 3, 4, 5, 6, 7]}
							centeredIndexes={[2]}
							data={[
								a.date_id,
								fromUnix(a.date).toFormat('dd/MMM/yy HH:mm'),
								addCommas(a.tickets, false),
								addCommas(a.total),
								addCommas(a.total_commission),
								addCommas(a.total_tickets),
								addCommas(a.total_payout),
								addCommas(a.total_tickets-(a.total_payout-a.total_payout_return)),
							]}
						/>
					))}
				</Table.Body>
			</Table>
		)}
		<Modal open={shownModal===ModalType.METHOD_INFO} onClose={bindClick(setShownModal)} size='tiny'>
			<Modal.Header>{viewing_method?.method_name || <i>Sin forma de pago</i>}</Modal.Header>
			{!!viewing_method && (
				<Modal.Content>
					<Table fitted details className='last' top>
						<Table.Body>
							<Table.Row data={['ID', (viewing_method.method_id || <Icon name="minus" color='grey' />)]} />
							<Table.Row data={['Boletos', addCommas(viewing_method.tickets, false)]} />
							<Table.Divider />
							<Table.Row data={['Total', addCommas(viewing_method.total)]} />
							<Table.Row data={['Promotor', addCommas(viewing_method.total_tickets)]} />
							<Table.Row data={['Comisión', addCommas(viewing_method.total_commission)]} />
							<Table.Divider />
							<Table.Row data={['Conciliado', <>
								{addCommas(viewing_method.total_deposit)}
								<span style={{ marginLeft: 5, color: 'gray', fontSize: 12 }}>({addCommas((viewing_method.total_deposit*100)/viewing_method.total)}%)</span>
							</>]} />
							<Table.Row data={['Faltante', addCommas(Math.max(0, viewing_method.total-viewing_method.total_deposit))]} />
						</Table.Body>
					</Table>
					<Button 
						color='black'
						text='Ver conciliación faltante'
						as={Link}
						to={`/conciliacion/events/${props.event.event_id}/${(viewing_method.method_id || -1)}`}
						target={'_blank'}
						style={{ display: 'block', margin: 'auto', width: 250, marginTop: 15 }} 
					/>
				</Modal.Content>
			)}
			<Modal.Actions>
				<Button text='Cerrar' onClick={bindClick(setShownModal)} />
			</Modal.Actions>
		</Modal>
		<Modal open={shownModal===ModalType.PAYOUT_CREATE && !!dates} onClose={bindClick(setShownModal)} size='small'>
			<Modal.Header>Crear liquidación</Modal.Header>
			{!!payoutForm && !!dates && (
				<Modal.Content>
					<Dropdown label='Tipo de liquidación' placeholder='Tipo de liquidación' value={payoutForm.payout_type} onValueChange={onPayoutFormChange('payout_type')} items={[
						{ value: 1, text: 'Liquidación' },
						{ value: 10, text: 'Anticipo' },
						...(hasAccess(UserAccess.CONTABILIDAD.CREATE_RETURN_PAYOUT) ? [
							{ value: 20, text: 'Retorno' },
						] : [])
					]} />
					<Field amount={2} style={{ marginBottom: 15, marginTop: 15 }}>
						<Field>
							<Checkbox label="Agendar transferencia" checked={payoutForm.scheduled && !payoutForm.completed} disabled={payoutForm.completed} onChange={onPayoutFormChange('scheduled')} />
						</Field>
						{hasAccess(UserAccess.EVENTS.CREATE_COMPLETED_PAYOUT) && (
							<Field>
								<Checkbox label="Liquidación completada" checked={payoutForm.completed} onChange={onPayoutFormChange('completed')} />
							</Field>
						)}
					</Field>
					{(!!payoutForm.scheduled || !!payoutForm.completed) && (
						<Input label='Fecha de deposito' calendar={{ 
							date: payoutForm.date_scheduled ? fromUnix(payoutForm.date_scheduled).toJSDate() : now.toJSDate(),
							onSelected: d=>{
								onPayoutFormChange('date_scheduled')(d.getTime()/1000)
							},
							mode: 'date' 
						}} />
					)}
					{!!!payoutForm.completed && (
						<Dropdown search
							label='Cuenta bancaria'
							loading={bankAccounts===null} 
							disabled={bankAccounts===null}
							value={payoutForm.account_id}
							onValueChange={onPayoutFormChange('account_id')}
							placeholder='Selecciona la cuenta bancaria'
							style={{ marginBottom: 10 }}
							items={[
								{ text: (<i>Nueva cuenta bancaria</i>), onClick: showAddAccount, value: null },
								...(bankAccounts ? bankAccounts.map(a=>({
									text: a.card_number ? (
										`${a.name_account || a.bank} **${a.card_number.slice(-4)}`
									) : a.clabe ? (
										`${a.name_account || a.bank} **${a.clabe.slice(-5)}`
									) : null,
									iconName: (a.card_number ? 'credit-card' : 'building-columns') as IconName,
									content: <div key={`BKA-${a.account_id}`}>
										{a.card_number ? (
											`${a.name_account || a.bank} **${a.card_number.slice(-4)}`
										) : a.clabe ? (
											`${a.name_account || a.bank} **${a.clabe.slice(-5)}`
										) : null}
									</div>, value: a.account_id
								})) : [])
							]}
						/>
					)}
					<Input textarea textareaRows={3} onChange={onPayoutFormChange('comments')} value={payoutForm.comments} label={'Descripción'} placeholder='Comentarios adicionales sobre la liquidación' />
					{/* <Dropdown
						value={event_business.length===1 ? event_business[0].business_id : null}
						loading={!businessSources}
						style={{ marginBottom: 10 }}
						label={'Empresa'}
						items={event_business?.map(a=>(
							{ text: a.business_name, value: a.business_id }
						))}
					/> */}
					<Dropdown
						value={null}
						label='Calendarios' 
						comment={filtered ? 'Mostrando solo calendarios filtrados' : null}
						placeholder='Selecciona el calendario para agregar'
						items={filtered_dates.filter(a=>(!payoutForm || payoutForm.amounts.findIndex(b=>b.date_id==a.date_id)===-1)).map(a=>{
							var total_remaining = a.total_tickets-(a.total_payout-a.total_payout_return);
							var payout_total = payoutForm.amounts.find(b=>b.date_id===a.date_id);
							var disabled = total_remaining<=0 && payoutForm.payout_type===PayoutType.NORMAL;
							if(payout_total) return null;
							return {
								value: a.date_id,
								disabled: disabled,
								text: `[${a.date_id}] ${fromUnix(a.date).toFormat('dd/MMM/yy HH:mm')}`,
								meta: `$${addCommas(total_remaining)}`,
								onClick: disabled ? null : ()=>{
									var pf = {...payoutForm};
									pf.amounts.push({
										date_id: a.date_id,
										date: a.date,
										amount: total_remaining.toString(),
									});
									setPayoutForm(pf);
								}
							}
						})}
					/>
					{payoutForm.amounts && payoutForm.amounts.length>0 && <>
						{payoutForm.payout_type===PayoutType.RETORNO && (
							<Message centered text='Las liquidaciones de tipo retorno pueden liquidar hasta la comisión de AREMA.' type='info' size="small" style={{ marginTop: 10 }} />
						)}
						<Table fitted style={{ marginTop: 15 }}>
							<Table.Head collapsingIndexes={[0, 1, 3, 4]} data={[
								['ID', 'Calendario', 'Liquidación', 'Liquidable', <Icon name="remove" />]
							]} />
							<Table.Body>
								{payoutForm.amounts.map(a=>{
									var date = dates.find(b=>b.date_id==a.date_id);
									if(!date) return null;
									var total_income = date.total_tickets;
									var total_remaining = date.total_tickets-(date.total_payout-date.total_payout_return);
									var input_error = total_remaining<parseFloat(a.amount) && payoutForm.payout_type!==PayoutType.ADELANTO;
									return (
										<Table.Row collapsingIndexes={[0, 1, 3, 4]} data={[
											a.date_id,
											fromUnix(a.date).toFormat('dd/MMM/yy HH:mm'),
											<Input 
												style={{ margin: 0 }}
												value={Number.isNaN(a.amount) ? '' : a.amount}
												error={input_error}
												onChange={v=>{
													var pf = {...payoutForm};
													var ax = pf.amounts.findIndex(b=>b.date_id==a.date_id);
													pf.amounts[ax].amount = v;
													setPayoutForm(pf);
												}}
											/>,
											addCommas(total_remaining),
											<Button color='black' size='tiny' iconName='remove' icon onClick={()=>{
												var pf = {...payoutForm};
												pf.amounts = pf.amounts.filter(b=>b.date_id!==a.date_id);
												setPayoutForm(pf);
											}} />
										]} />
									)
								})}
							</Table.Body>
						</Table>
						<Stat style={{ marginTop: 20 }}>
							<Stat.Value text={addCommas(payoutForm.amounts.reduce((a,b)=>a+parseFloat(b.amount), 0) || 0)} />
							<Stat.Label text={'Total liquidación'} />
						</Stat>
					</>}
					<Message list={modalPrompts} type='error' />
				</Modal.Content>
			)}
			<Modal.Actions>
				<Button basic text='Cerrar' onClick={bindClick(setShownModal)} />
				<Button color='black' text='Crear liquidación' onClick={createPayout} />
			</Modal.Actions>
		</Modal>
		<Modal open={shownModal===ModalType.FILTER_DATES} onClose={bindClick(setShownModal)} size='tiny'>
			<Modal.Header>Filtrar fechas</Modal.Header>
			<Modal.Content>
				<SegmentControl 
					selected={[filterType]}
					onClick={setFilterType}
					style={{ marginBottom: 10 }}
					segments={[
						{ value: FilterType.DATES, text: t('common.event_date') },
						{ value: FilterType.RANGE, text: t('event_balance.date_range') },
					]}
				/>
				{allDates===null ? (
					modalPrompts && modalPrompts.length>0 ? (
						<Header text={t('common.error')} subtext={modalPrompts[0]} style={{ marginTop: 15 }} />
					) : (
						<Header text={t('common.loading')} loading style={{ marginTop: 15 }} />
					)
				) : filterType===FilterType.DATES ? (
					<Table fitted striped>
						<Table.Head title={t('common.event_date')} />
						<Table.Body>
							{allDates.length==0 ? (
								<Table.Cell empty row>{t('event_balance.no_dates')}</Table.Cell>
							) : AllDatesList}
						</Table.Body>
						{!!(allDates && allDates.length>PAGE_SIZE) && (
							<Table.Footer>
								<Table.Cell row style={{ textAlign: 'right' }} colSpan={100}>
									<Pagination page={filterPage} onPageChange={setFilterPage} pageCount={Math.ceil(allDates.length/PAGE_SIZE)} />
								</Table.Cell>
							</Table.Footer>
						)}
					</Table>
				) : <>
					<Input label={t('common.start_date')} readonly calendar={{
						initialDate: now.plus({ hours: -24 }).toJSDate(),
						date: filterRange?.start?.toJSDate() || null,
						onSelected: v=>{
							setFilterRange(a=>({ ...a, start: DateTime.fromJSDate(v) }))
						},
						format: 'dd/MMM/yyyy',
						mode: 'date',
					}} />
					<Input label={t('common.end_date')} readonly calendar={{
						initialDate: now.toJSDate(),
						onSelected: v=>{
							setFilterRange(a=>({ ...a, end: DateTime.fromJSDate(v) }))
						},
						date: filterRange?.end?.toJSDate() || null,
						format: 'dd/MMM/yyyy',
						mode: 'date',
					}} />
					<Message type='info' centered>
						<Trans i18nKey={'event_balance.filtered_prediction'} count={filter_prediction} />
					</Message>
				</>}
			</Modal.Content>
			<Modal.Actions>
				<Button text={t('common.close')} basic onClick={bindClick(setShownModal)} />
				<Button text={t('common.filter')} color='black' onClick={filterDates} />
			</Modal.Actions>
		</Modal>
		{hasAccess(UserAccess.CONTABILIDAD.VIEW_PAYOUTS) && (
			<Modal open={shownModal===ModalType.PAYOUT_VIEW && !!payouts} onClose={bindClick(setShownModal)} size='tiny'>
				<Modal.Header>Liquidación</Modal.Header>
				{!!selectedPayout && <Modal.Content>
					<Table fitted details top>
						<Table.Body data={[
							['ID', selectedPayout.payout_id],
							['Tipo', selectedPayout.payout_type_name],
							['Cantidad', addCommas(selectedPayout.amount)],
							['Calendarios', addCommas(selectedPayout.dates.length, false)],
							...(selectedPayout.comments && selectedPayout.comments.trim().length>0 ? [['Comentarios', selectedPayout.comments]] : []),
							// null,
							['Aprobada', <>
								<i className={classNames("icon", {
									'red remove': !selectedPayout.approved && !selectedPayout.completed,
									'green check': !!selectedPayout.approved || selectedPayout.completed
								})}></i> 
								{(!!selectedPayout.approved || !!selectedPayout.completed) ? 'No aprobada' : 'Aprobada'}
							</>],
							['Estatus', <>
								<i className={classNames("icon", {
									'yellow clock': !selectedPayout.completed,
									'green check': !!selectedPayout.completed
								})}></i> 
								{!!selectedPayout.completed ? 'Completada' : 'Pendiente'}
							</>],
							// null,
							['Creador', <>
								<Link className='normal' to={`/users/${selectedPayout.user_creator}`} target='_blank'>
									{selectedPayout.user_creator_name}
								</Link>
								<div className="meta">
									{fromUnix(selectedPayout.date_created).toFormat('dd/MM/yy HH:mm')}
								</div>
							</>],
							...(!!selectedPayout.approved ? [['Aprobador', <>
								<Link className='normal' to={`/users/${selectedPayout.user_approved}`} target='_blank'>
									{selectedPayout.user_approved_name}
								</Link>
								<div className="meta">
									{fromUnix(selectedPayout.date_approved).toFormat('dd/MM/yy HH:mm')}
								</div>
							</>]] : []),
							...(!!selectedPayout.completed ? [['Completed', <>
								<Link className='normal' to={`/users/${selectedPayout.user_completed}`} target='_blank'>
									{selectedPayout.user_completed_name}
								</Link>
								<div className="meta">
									{fromUnix(selectedPayout.date_completed).toFormat('dd/MM/yy HH:mm')}
								</div>
							</>]] : [])
						]} />
					</Table>
					<Button 
						color='black'
						text='Ver información completa'
						as={Link}
						to={`/payouts/${selectedPayout.payout_id}`}
						target={'_blank'}
						style={{ display: 'block', margin: 'auto', width: 250, marginTop: 15 }} 
					/>
				</Modal.Content>}
				<Modal.Actions>
					<Button onClick={bindClick(setShownModal)} text='Cerrar' />
				</Modal.Actions>
			</Modal>
		)}
		<BankAccountModal open={createAccountModal} onClose={bindClick(setCreateAccountModal)} onCreate={onBankAccountCreated} promoter={props.event.promoter_id} />
		<EventDepositSourcesModal
			open={shownModal===ModalType.DEPOSIT_SOURCES}
			eventId={props.event.event_id}
			onClose={bindClick(setShownModal)}
		/>
		<EventDateStatusModal
			title={selectedDate ? `${t('estven.title')} - ${fromUnix(selectedDate.date).toFormat('dd/MMM/yy HH:mm')}` : ''}
			open={shownModal===ModalType.DATE_SECTION_STATUS && !!selectedDate}
			onClose={bindClick(setShownModal)}
			dateId={selectedDate?.date_id}
		/>
	</div>
}

export default EventBalance;